<script setup>
import Button from "@/base-components/Button";
import Lucide from "@/base-components/Lucide";
import {useFetch} from "@/composables/useFetch.js";
import {onMounted, ref} from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import "@bhplugin/vue3-datatable/dist/style.css";
import {useRoute, useRouter} from "vue-router";
import {uri} from "@/constants/config.js";
import "vue-awesome-paginate/dist/style.css";
import BasePagination from "@/base-components/Table/BasePagination.vue";
import {useI18n} from "vue-i18n";
import AddIPGDialog from "@/pages/manager/financial/ipg/AddIPGDialog.vue";
import ViewIPGDialog from "@/pages/manager/financial/ipg/ViewIPGDialog.vue";
import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
import EditIPGDialog from "@/pages/manager/financial/ipg/EditIPGDialog.vue";

const {t: $t} = useI18n()
const route = useRoute()
const router = useRouter()
const isLoadingFlag = ref(true)
const showDeleteIPGFlag = ref(false)
const ipgList = ref([])
const cols = [
  {field: "counter", title: "#"},
  {field: "id", title: $t('title.id')},
  {field: "displayName", title: $t('title.title')},
  {field: "title", title: $t('title.name')},
  {field: "plugin", title: $t('title.plugin')},
  {field: "sandbox", title: $t('title.sandbox')},
  {field: "status", title: $t('title.status')},
  {field: "action", title: $t('title.action')},
]
const pageOptions = ref({
  count: 1,
  limit: 10,
  page: 1
})

function onSuccessChangeIPG() {
  setEditIPGFlag(false);
  setOpenIPGFlag(false);
  Object.assign(currentIPG.value, defaultIPG)
  fetchData()
}

const getIPGList = async () => {
  isLoadingFlag.value = true
  let response = null
  try {
    const {
      data,
      error
    } = await useFetch(`${uri.admin.financial.ipg.list}?page=${pageOptions.value.page}`, {method: "GET"});
    if (data) {
      response = data
      pageOptions.value.count = data.meta.total
      pageOptions.value.limit = data.meta.perPage
    }
  } catch (err) {
    console.log(err)
  }
  return response
}


const fetchData = async () => {
  try {
    const [ipg] = await Promise.all([
      getIPGList(),
    ]);
    ipgList.value = ipg.data;
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    isLoadingFlag.value = false
  }
}
const currentIPG = ref({})
const defaultIPG = {}
const openIPGFlag = ref(false)
const openViewIPGFlag = ref(false)
const openEditIPGFlag = ref(false)

function setOpenIPGFlag(value) {
  openIPGFlag.value = value
}

function setEditIPGFlag(value) {
  openEditIPGFlag.value = value
}

function openIPGDialog() {
  openIPGFlag.value = true
}

function openViewIPGDialog(ipg) {
  Object.assign(currentIPG.value, ipg)
  openViewIPGFlag.value = true
}

function openEditIPGDialog(ipg) {
  Object.assign(currentIPG.value, ipg)
  openEditIPGFlag.value = true
}

function rowClick(ipg) {
}

function confirmationDeleteIPG(ipg){
  showDeleteIPGFlag.value = true
  currentIPG.value = ipg
  Object.assign(currentIPG.value, ipg)
}

function cancelDeleteIPG(){
  showDeleteIPGFlag.value = false
  Object.assign(currentIPG.value, defaultIPG)
}

function resetData(){
  fetchData()
  setEditIPGFlag(false)
}

async function confirmDeleteIPG() {
  showDeleteIPGFlag.value = false
  isLoadingFlag.value = true
  try {
    const {data, error} = await useFetch(`${uri.admin.financial.ipg.delete}/${currentIPG.value.id}`, {method: "DELETE"});
    if (data) {
      await fetchData()
    }else{
      isLoadingFlag.value = false
    }
  } catch (err) {
    console.log(err)
    isLoadingFlag.value = false
  }
}

// Fetch data when the component mounts
onMounted(fetchData);

</script>
<template>
  <section>

    <ConfirmationDialog
        :close="cancelDeleteIPG"
        :show="showDeleteIPGFlag"
        :accept="confirmDeleteIPG"
        :deny="cancelDeleteIPG"
    >
      {{ $t('message.are-you-sure') }}
    </ConfirmationDialog>

    <AddIPGDialog
        v-if="openIPGFlag"
        @close="setOpenIPGFlag(false)"
        @onSuccess="onSuccessChangeIPG"
        :show="openIPGFlag"
    />
    
    <EditIPGDialog
        v-if="openEditIPGFlag"
        @close="resetData"
        @onSuccess="onSuccessChangeIPG"
        :show="openEditIPGFlag"
        :ipg="currentIPG"
    />
 
    <ViewIPGDialog
        v-if="openViewIPGFlag"
        :ipg="currentIPG"
        @close="openViewIPGFlag = false"
        :show="openViewIPGFlag"
    />


    <div class="grid grid-cols-12 gap-6 mt-8">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">

          <div
              class="flex p-5 border-b sm:flex-row text-slate-500 border-slate-200/60 text-end w-full justify-end items-end"
          >
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="fetchData">
              <Lucide icon="RefreshCw" class="w-6 h-6"/>
            </div>
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="openIPGDialog">
              <Lucide icon="PlusCircle" class="w-6 h-6"/>
            </div>
          </div>
          <div class="mb-5 relative">
          </div>

          <vue3-datatable
              :columns="cols"
              :rows="ipgList"
              :loading="isLoadingFlag"
              :totalRows="pageOptions.count"
              :isServerMode="true"
              :pageSize="pageOptions.limit"
              :pagination="false"
              :sortable="false"
              class="advanced-table px-4 text-start items-start justify-start"
              rowClass="cursor-pointer"
              @rowClick="rowClick"
          >
            <!-- Empty header slot to remove the header -->
            <template #header>
              <!-- No content here will hide the header -->
            </template>
            <!-- Empty footer slot to remove the footer -->
            <template #footer>
              <!-- No content here will hide the footer -->
            </template>

            <template #counter="data,key">
              <div class="text-start">

                {{
                  (ipgList.indexOf(data.value) + 1) + ((pageOptions.page - 1) * pageOptions.limit)
                }}
              </div>
            </template>

            <template #ipg="data,key">
              <div class="text-start">
                {{ ipgList.find(item => item.id === data.value.ipgId)?.name ?? '-' }}
              </div>
            </template>

            <template #id="data,key">
              <div class="text-start">
                {{ data.value.id }}
              </div>
            </template>

            <template #displayName="data,key">
              <div class="text-start">
                {{ data.value.displayName }}
              </div>
            </template>
            <template #title="data,key">
              <div class="text-start">
                {{ data.value.name }}
              </div>
            </template>
            <template #plugin="data,key">
              <div class="text-start">
                {{ data.value.plugin }}
              </div>
            </template>
            <template #sandbox="data,key">
              <div class="text-start">
                <Button variant="outline-dark" size="sm" class="w-16">
                  {{ data.value.adapterConfig.sandboxed ? $t('button.yes') : $t('button.no') }}
                </Button>
              </div>
            </template>
            <template #status="data,key">
              <div class="text-start">
                <Button :variant="data.value.active?'outline-primary':'outline-warning'" size="sm" class="w-16">
                  {{ data.value.active ? $t("title.active") : $t("title.inactive") }}
                </Button>
              </div>
            </template>
            <template #action="data,key">
              <div class="text-start flex gap-3">
                <div
                    @click="openViewIPGDialog(data.value)">
                  <Lucide icon="Eye" class="w-6 h-6 text-gray-700"/>
                </div>
                <div
                    @click="confirmationDeleteIPG(data.value)">
                  <Lucide icon="Trash2" class="w-6 h-6 text-gray-700"/>
                </div>
                <div
                    @click="openEditIPGDialog(data.value)">
                  <Lucide icon="Edit" class="w-6 h-6 text-gray-700"/>
                </div>
              </div>
            </template>

          </vue3-datatable>
          <div
              class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>

              <BasePagination
                  v-if="pageOptions.count>pageOptions.limit"
                  :page-options="pageOptions"
                  @page-change="fetchData"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0">
            </div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>
